import React, { useState } from 'react';
import {
    Grid,
    Typography,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OutletIcon from '@mui/icons-material/Outlet';
import CompaniesOptionsForm from './options/CompaniesOptionsForm';
import { format } from 'date-fns';
import '@fontsource/roboto';

const formatUpdateTime = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return format(d, 'yyyy-MM-dd HH:mm');
    } catch {
        return iso;
    }
};

const Companies = () => {
    const [data, setData] = useState([]);
    const [selectedFields, setSelectedFields] = useState([]);
    const [name, setName] = useState('RixEngine Companies');

    const handleGenerate = (list, fields) => {
        setData(Array.isArray(list) ? list : []);
        setSelectedFields(Array.isArray(fields) ? fields : []);
        setName(`RixEngine Companies - ${format(new Date(), 'yyyy-MM-dd')}`);
    };

    const rows = data.map((row, index) => ({
        id: row.partner_id ?? index,
        partner_id: row.partner_id,
        partner_name: row.partner_name ?? '',
        type: row.type ?? '',
        type_desc: row.type_desc ?? '',
        update_time: formatUpdateTime(row.update_time),
        advertisers_count: (row.advertisers ?? []).length,
        publishers_count: (row.publishers ?? []).length,
        advertisers: row.advertisers ?? [],
        publishers: row.publishers ?? [],
    }));

    const allColumns = [
        { field: 'partner_id', headerName: 'Partner ID' },
        { field: 'partner_name', headerName: 'Partner Name' },
        { field: 'type_desc', headerName: 'Type' },
        { field: 'update_time', headerName: 'Update Time' },
        { field: 'advertisers_count', headerName: 'Advertisers' },
        { field: 'publishers_count', headerName: 'Publishers' },
        {
            field: 'advertisers',
            headerName: 'Advertisers (Buyers)',
            render: (row) => {
                const list = row.advertisers ?? [];
                const text = list.map((a) => `${a.buyer_name} (${a.buyer_id})`).join(', ');
                return (
                    <Box sx={{ py: 0.5, whiteSpace: 'normal', wordBreak: 'break-word' }} title={text}>
                        {text || '—'}
                    </Box>
                );
            },
        },
        {
            field: 'publishers',
            headerName: 'Publishers (Sellers)',
            render: (row) => {
                const list = row.publishers ?? [];
                const text = list.map((p) => `${p.seller_name} (${p.seller_id})`).join(', ');
                return (
                    <Box sx={{ py: 0.5, whiteSpace: 'normal', wordBreak: 'break-word' }} title={text}>
                        {text || '—'}
                    </Box>
                );
            },
        },
    ];

    const columns =
        selectedFields.length > 0
            ? allColumns.filter((col) => selectedFields.includes(col.field))
            : allColumns;

    const downloadData = rows.map((r) => {
        const base = {
            partner_id: r.partner_id,
            partner_name: r.partner_name,
            type_desc: r.type_desc,
            update_time: r.update_time,
            advertisers_count: r.advertisers_count,
            publishers_count: r.publishers_count,
            advertisers: (r.advertisers ?? []).map((a) => `${a.buyer_name} (${a.buyer_id})`).join('; '),
            publishers: (r.publishers ?? []).map((p) => `${p.seller_name} (${p.seller_id})`).join('; '),
        };
        if (selectedFields.length > 0) {
            return Object.fromEntries(selectedFields.map((k) => [k, base[k]]));
        }
        return base;
    });

    return (
        <div>
            <Grid
                container
                spacing={2}
                sx={{
                    padding: 2,
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '4px',
                    paddingLeft: 0,
                    paddingTop: 0,
                    marginLeft: '-10px',
                    marginTop: '-5px',
                    boxShadow: '0 0 10px rgba(0, 0, 0, 0.08)',
                }}
            >
                <Grid item xs={12} md={12} sx={{ width: '100%' }}>
                    <Accordion sx={{ width: '100%' }}>
                        <AccordionSummary
                            expandIcon={<InfoOutlinedIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                            sx={{
                                backgroundColor: '#f0f3f5',
                                width: '100%',
                                height: '35px !important',
                                minHeight: '25px !important',
                            }}
                        >
                            Explanations
                        </AccordionSummary>
                        <AccordionDetails sx={{ width: '100%' }}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ marginBottom: '1rem' }}>
                                    RixEngine Companies – partners with advertisers and publishers
                                </Typography>
                                <Typography variant="body1" sx={{ marginBottom: '1rem', marginLeft: '1rem' }}>
                                    This report shows all RixEngine partners (companies) synced from the Mobupps RTB Console API.
                                    Each partner can have multiple Advertisers (buyers) and Publishers (sellers).
                                    Mapping to iRTB companies will be available in the iRTB DB → Companies section.
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>

            <Grid
                container
                spacing={2}
                sx={{
                    padding: 2,
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '4px',
                    paddingLeft: 0,
                    marginLeft: '-10px',
                    marginTop: '20px',
                    boxShadow: '0 0 10px rgba(0, 0, 0, 0.08)',
                }}
            >
                <Grid item xs={12} md={12} sx={{ width: '100%' }}>
                    <CompaniesOptionsForm onGenerate={handleGenerate} />
                </Grid>
                <Grid item xs={12} md={12}>
                    {data.length === 0 && selectedFields.length > 0 && (
                        <Typography variant="h4" component="h2" align="center">
                            <OutletIcon fontSize="large" /> NO DATA
                        </Typography>
                    )}
                    {data.length > 0 && (
                        <TableContainer
                            component={Paper}
                            sx={{
                                padding: 2,
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                border: '6px',
                                paddingLeft: 0,
                                paddingTop: 0,
                                marginLeft: '0px',
                                marginTop: '10px',
                                boxShadow: '0 0 1px rgba(0, 0, 0, 0.2)',
                            }}
                        >
                            <Table>
                                <TableHead sx={{ backgroundColor: '#f0f3f5' }}>
                                    <TableRow>
                                        {columns.map((col) => (
                                            <TableCell key={col.field}>{col.headerName}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow key={row.id}>
                                            {columns.map((col) => (
                                                <TableCell key={col.field}>
                                                    {col.render
                                                        ? col.render(row)
                                                        : row[col.field] ?? '—'}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Grid>
            </Grid>
        </div>
    );
};

export default Companies;
